import React, { useState } from "react";
import { Dialog, Box, Typography, Button, IconButton, LinearProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardIos";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PublicIcon from "@mui/icons-material/Public";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BuildIcon from "@mui/icons-material/Build";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import { C } from "./reportingUtils";

// "Walk me" style, plain-language explanation of each BI & Reporting feature.
export const BI_FEATURES = [
  { key: "portfolio", label: "Portfolio Health", Icon: MonitorHeartIcon, color: C.blue,
    blurb: "Your whole book at a glance — total counterparties, recommended vs approved exposure, and your average ARC risk score. Start here to know your portfolio's health in one screen." },
  { key: "risk", label: "Risk Distribution", Icon: DonutLargeIcon, color: C.violet,
    blurb: "See how your counterparties spread across risk bands — low, medium and high. Spot a build-up in the risky buckets before it becomes a problem." },
  { key: "limits", label: "Limits & Exposure", Icon: AccountBalanceWalletIcon, color: C.green,
    blurb: "Track how much credit you've recommended versus approved, and where your largest exposures sit — so your limits stay aligned with your appetite." },
  { key: "geo", label: "Sectors & Geography", Icon: PublicIcon, color: C.amber,
    blurb: "Break exposure down by industry and region to uncover hidden concentration — like too much riding on a single sector or country." },
  { key: "insights", label: "AI Insights", Icon: AutoAwesomeIcon, color: C.blue,
    blurb: "A plain-language read on your portfolio: automatic anomaly detection, narrative summaries, and a 3-month forecast — the analyst's take, written for you." },
  { key: "builder", label: "Report Builder", Icon: BuildIcon, color: C.violet,
    blurb: "Build any report by choosing a measure, a grouping and filters — then save it. Or just ask in plain English and let AI assemble it for you." },
  { key: "alerts", label: "Alerts", Icon: NotificationsActiveIcon, color: C.red,
    blurb: "Set thresholds — say, high-risk share above 20% — and get notified the moment your portfolio crosses them. No more manual watching." },
  { key: "schedules", label: "Scheduled Exports", Icon: ScheduleSendIcon, color: C.green,
    blurb: "Have reports delivered to your inbox automatically — daily, weekly or monthly — so the numbers come to you." },
];

export default function BIWalkthrough({ open, onClose, onFinish }) {
  const [step, setStep] = useState(0);
  const total = BI_FEATURES.length;
  const f = BI_FEATURES[step];
  const Icon = f.Icon;
  const last = step === total - 1;

  const close = () => { setStep(0); onClose && onClose(); };
  const finish = () => { setStep(0); (onFinish || onClose) && (onFinish || onClose)(); };

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <Box sx={{ position: "relative" }}>
        <IconButton onClick={close} size="small" sx={{ position: "absolute", top: 10, right: 10, zIndex: 2, color: C.muted }}>
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* progress */}
        <LinearProgress variant="determinate" value={((step + 1) / total) * 100}
          sx={{ height: 4, backgroundColor: "#EEF2F9", "& .MuiLinearProgress-bar": { backgroundColor: f.color } }} />

        <Box sx={{ px: 4, pt: 4, pb: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: C.muted, mb: 2 }}>
            Walk me through it · {step + 1} of {total}
          </Typography>

          <Box sx={{ width: 66, height: 66, borderRadius: "18px", mx: "auto", display: "grid", placeItems: "center",
            background: `${f.color}14`, mb: 2.5 }}>
            <Icon sx={{ fontSize: 32, color: f.color }} />
          </Box>

          <Typography sx={{ fontSize: 21, fontWeight: 800, color: C.ink, mb: 1 }}>{f.label}</Typography>
          <Typography sx={{ fontSize: 14.5, lineHeight: 1.6, color: C.slate, maxWidth: 440, mx: "auto", minHeight: 92 }}>
            {f.blurb}
          </Typography>

          {/* dots */}
          <Box sx={{ display: "flex", justifyContent: "center", gap: 0.75, my: 2.5 }}>
            {BI_FEATURES.map((s, i) => (
              <Box key={s.key} onClick={() => setStep(i)}
                sx={{ width: i === step ? 22 : 7, height: 7, borderRadius: 4, cursor: "pointer",
                  background: i === step ? f.color : "#D8E0EC", transition: "all .2s" }} />
            ))}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
              startIcon={<ArrowBackIcon sx={{ fontSize: "13px !important" }} />}
              sx={{ textTransform: "none", color: C.muted, visibility: step === 0 ? "hidden" : "visible" }}>
              Back
            </Button>
            {last ? (
              <Button onClick={finish} variant="contained"
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 3, background: C.blue }}>
                Got it
              </Button>
            ) : (
              <Button onClick={() => setStep((s) => Math.min(total - 1, s + 1))} variant="contained"
                endIcon={<ArrowForwardIcon sx={{ fontSize: "13px !important" }} />}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 3, background: C.blue }}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
